import { LowerCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
    AbuseReportType,
    EntityType,
    LikeType,
    UserStoreService,
} from '@polaris-customerweb/sharedcomponents.core';
import { LogService } from '@polaris-customerweb/sharedcomponents.shared';
import { find, kebabCase } from 'lodash-es';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';
import { ForceCollectionRefresh } from 'src/app/components/common/collection/collection.actions';
import {
    AddMyCommunityItem,
    RemoveMyCommunityItem,
    SetMyCommunityItems,
} from 'src/app/components/community/activity/community-item.actions';
import { selectMyCommunityItems } from 'src/app/components/community/activity/community-item.reducer';
import { ForceGeofenceRefresh } from 'src/app/components/geofence/geofence.actions';
import { ForcePlannedRideRefresh } from 'src/app/components/planned-ride/planned-ride.actions';
import { ForceTrackedRideRefresh } from 'src/app/components/tracked-ride/tracked-ride.actions';
import { ForceWaypointRefresh } from 'src/app/components/waypoint/waypoint.actions';
import { AppState } from 'src/app/models/app-state';
import { Collection } from 'src/app/models/collection';
import { CommunityItem } from 'src/app/models/community-item';
import { CommunityItemSearchFilter } from 'src/app/models/community-item-search-filter';
import { NewPostComponent } from '../components/community/new-post/new-post.component';
import { ShareToCommunityDialogOptions } from '../components/shared/components/share-to-community-dialog/share-to-community-dialog-options';
import { ShareToCommunityDialogComponent } from '../components/shared/components/share-to-community-dialog/share-to-community-dialog.component';
import { SelectedItems } from '../models/selectedItems';
import { RemoveWhiteSpacePipe } from '../pipes/remove-whitespace.pipe';
import { ClearCacheCommunityFilter } from '../state/community/community-filter/community-filter.actions';
import { selectCommunityFilter } from '../state/community/community-filter/community-filter.selectors';
import {
    RemoveItemFromCommunityLists,
    UpdateItemInCommunityLists,
} from '../state/community/community-list/community-list.actions';
import { selectCommunityList } from '../state/community/community-list/community-list.selectors';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { CommunityItemResourceService } from './community-item-resource.service';

@Injectable({ providedIn: 'root' })
export class CommunityItemService {
    static readonly defaultRadiusInMiles = 500;
    static readonly defaultRadiusInKilometers = 1000;
    static readonly earthCircumferenceInMeters = 40075000;

    readonly className = 'CommunityItemService';

    private hasFetchedMyCommunityItems = false;

    constructor(
        private logService: LogService,
        private matDialog: MatDialog,
        private resource: CommunityItemResourceService,
        private router: Router,
        private store: Store<AppState>,
        private userStoreService: UserStoreService,
        private removeWhiteSpace: RemoveWhiteSpacePipe,
        private lowerCase: LowerCasePipe,
        private userAuthService: UserAuthService
    ) {}

    // #region Get community item
    getCommunityFeedItems$(
        filter: CommunityItemSearchFilter
    ): Observable<CommunityItem[]> {
        return this.userAuthService.isAuthenticated$.pipe(
            switchMap((isAuth) =>
                this.resource.getCommunityItems(filter, isAuth)
            )
        );
    }

    getCommunityFilter$(
        context: string,
        type: EntityType[]
    ): Observable<CommunityItemSearchFilter> {
        return this.store.select(selectCommunityFilter(context)).pipe(
            take(1),
            map((communityFilter: CommunityItemSearchFilter) => {
                if (!communityFilter) {
                    communityFilter = new CommunityItemSearchFilter(context);
                    communityFilter.type = type;
                    communityFilter.filterPastEvents = true;
                }

                // reset to page one if data is stale or there is no data yet
                if (!communityFilter.cacheDate || communityFilter.isStale()) {
                    communityFilter.pagination.initialize();
                }
                return communityFilter;
            })
        );
    }

    getCommunityItemListSelector$(
        context: string
    ): Observable<CommunityItem[]> {
        return this.store.select(selectCommunityList(context));
    }

    getFullCommunityItem(communityId: string): Observable<CommunityItem> {
        return this.userAuthService.isAuthenticated$.pipe(
            switchMap((isAuth) =>
                this.resource.getCommunityItemById(communityId, isAuth)
            ),
            switchMap((communityItem) => {
                if (!communityItem) {
                    return throwError(
                        () => new Error('community item not found')
                    );
                } else if (communityItem.linkedType === 'Event') {
                    return this.getFullRideEventForCommunityItem(communityItem);
                } else if (communityItem.linkedType === 'Collection') {
                    return this.getFullCollectionForCommunityItem(
                        communityItem
                    );
                } else {
                    return of(communityItem);
                }
            })
        );
    }

    //#endregion

    //#region Create a copy
    copyCommunityItem(communityItem: CommunityItem): Observable<string> {
        return this.resource.copyCommunityItem(communityItem.id).pipe(
            tap(() => {
                switch (communityItem.linkedType) {
                    case 'Collection':
                        this.store.dispatch(new ForceCollectionRefresh(true));
                        break;
                    case 'Geofence':
                        this.store.dispatch(new ForceGeofenceRefresh(true));
                        break;
                    case 'PlannedRide':
                        this.store.dispatch(new ForcePlannedRideRefresh(true));
                        break;
                    case 'TrackedRide':
                        this.store.dispatch(new ForceTrackedRideRefresh(true));
                        break;
                    case 'Waypoint':
                        this.store.dispatch(new ForceWaypointRefresh(true));
                        break;
                    default:
                        break;
                }
            })
        );
    }

    //#endregion

    //#region My community items
    getMyCommunityItems$(): Observable<CommunityItem[]> {
        if (!this.hasFetchedMyCommunityItems) {
            return this.resource
                .getMyCommunityItems({ includeFullDetail: true })
                .pipe(
                    switchMap((items) => {
                        this.store.dispatch(new SetMyCommunityItems(items));
                        return this.store.select(selectMyCommunityItems);
                    })
                );
        }

        return this.store.select(selectMyCommunityItems);
    }

    getMyCommunityItemByChildId(
        contentItemId: string
    ): Observable<CommunityItem | undefined> {
        return this.getMyCommunityItems$().pipe(
            take(1),
            map((communityItems) =>
                find(
                    communityItems,
                    (item) => item.content.contentItem.id === contentItemId
                )
            )
        );
    }

    openNewPostDialog(): void {
        this.matDialog
            .open(NewPostComponent, {
                maxWidth: '80vw',
                width: '800px',
            })
            .afterClosed()
            .pipe(
                exhaustMap((selectedItems: SelectedItems | string) => {
                    if (typeof selectedItems === 'string') {
                        return throwError(() => new Error(selectedItems));
                    }

                    const data: ShareToCommunityDialogOptions = {
                        userContentItem: selectedItems.userContentItems[0],
                    };

                    return this.matDialog
                        .open(ShareToCommunityDialogComponent, { data })
                        .afterClosed();
                })
            )
            .subscribe({
                error: (error: Error) => {
                    if (error.message !== 'cancel') {
                        this.logService.error(this.className, error);
                    }
                },
                next: (communityItem: CommunityItem | string) => {
                    if (communityItem instanceof CommunityItem) {
                        void this.router.navigate([
                            '/community',
                            'activity',
                            communityItem.id,
                            kebabCase(communityItem.linkedType),
                            communityItem.content.contentItem.id,
                        ]);
                    }
                },
            });
    }

    createCommunityItem(item: CommunityItem): Observable<CommunityItem> {
        return this.resource.createCommunityItem(item).pipe(
            tap(() => {
                // resetting filter to allow new item to be returned in search
                this.store.dispatch(ClearCacheCommunityFilter());
                this.store.dispatch(new AddMyCommunityItem(item));
            })
        );
    }

    updateCommunityItem(item: CommunityItem): Observable<CommunityItem> {
        return this.resource.updateCommunityItem(item).pipe(
            tap(() => {
                // resetting filter to allow new item to be returned in search
                this.store.dispatch(ClearCacheCommunityFilter());
                this.store.dispatch(new AddMyCommunityItem(item));
            })
        );
    }

    deleteCommunityItem(id: string): Observable<void> {
        return this.resource.deleteCommunityItem(id).pipe(
            tap(() => {
                // resetting filter to allow new item to be returned in search
                this.store.dispatch(ClearCacheCommunityFilter());
                this.store.dispatch(new RemoveMyCommunityItem(id));
            })
        );
    }

    // #endregion

    // #region Ratings, Likes and abuse
    rate(
        communityItem: CommunityItem,
        rating: number
    ): Observable<CommunityItem> {
        return this.resource.rate(communityItem.id, rating).pipe(
            map((communityFeedback) => {
                const updatedCommunityItem = communityItem.clone();
                updatedCommunityItem.communityFeedback = communityFeedback;
                return updatedCommunityItem;
            }),
            tap((updatedCommunityItem) => {
                this.store.dispatch(
                    UpdateItemInCommunityLists({ item: updatedCommunityItem })
                );
            })
        );
    }

    reportAbuse(
        communityId: string,
        abuseType: AbuseReportType = 'None'
    ): Observable<undefined> {
        return this.resource.reportAbuse(communityId, abuseType).pipe(
            tap(() => {
                this.store.dispatch(
                    RemoveItemFromCommunityLists({ id: communityId })
                );
            })
        );
    }

    like(
        communityItem: CommunityItem,
        like: LikeType
    ): Observable<CommunityItem> {
        return this.resource.like(communityItem.id, like).pipe(
            map((communityFeedback) => {
                const updatedCommunityItem = communityItem.clone();
                updatedCommunityItem.communityFeedback = communityFeedback;
                return updatedCommunityItem;
            }),
            tap((updatedCommunityItem) => {
                this.store.dispatch(
                    UpdateItemInCommunityLists({ item: updatedCommunityItem })
                );
            })
        );
    }

    setMostRecentlyViewedItem(context: string, name: string) {
        this.userStoreService.set(
            '_recentCommunityItem_' + context,
            this.lowerCase.transform(this.removeWhiteSpace.transform(name))
        );
    }

    getMostRecentlyViewedItem(context: string) {
        return this.userStoreService.get('_recentCommunityItem_' + context);
    }

    // #endregion

    private getFullRideEventForCommunityItem(
        communityItem: CommunityItem
    ): Observable<CommunityItem> {
        const rideEventId = communityItem.content.event.id;
        return this.resource
            .getRideEventContents(communityItem.id, rideEventId)
            .pipe(
                switchMap((contentCollection) => {
                    communityItem.content.event.setContentCollection(
                        contentCollection
                    );
                    return this.getContentsForAllRideEventCollections(
                        communityItem.id,
                        rideEventId,
                        communityItem.content.event.collections
                    );
                }),
                map((collections) => {
                    communityItem.content.event.collections = collections;
                    return communityItem;
                })
            );
    }

    private getContentsForAllRideEventCollections(
        communityItemId: string,
        rideEventId: string,
        collections: readonly Collection[]
    ): Observable<Collection[]> {
        if (!collections || collections.length === 0) {
            return of([]);
        }

        return forkJoin(
            collections.map((collection) => {
                return this.getContentsForRideEventCollection(
                    communityItemId,
                    rideEventId,
                    collection
                );
            })
        );
    }

    private getContentsForRideEventCollection(
        communityItemId: string,
        rideEventId: string,
        collection: Collection
    ): Observable<Collection> {
        return this.resource
            .getRideEventCollectionContents(
                communityItemId,
                rideEventId,
                collection.id
            )
            .pipe(
                map((contentCollection) => {
                    const resultCollection = collection.clone();
                    resultCollection.setContentCollection(contentCollection);
                    return resultCollection;
                })
            );
    }

    private getFullCollectionForCommunityItem(
        communityItem: CommunityItem
    ): Observable<CommunityItem> {
        const collectionId = communityItem.content.collection.id;
        return this.resource
            .getCollectionContents(communityItem.id, collectionId)
            .pipe(
                map((contentCollection) => {
                    communityItem.content.collection.setContentCollection(
                        contentCollection
                    );
                    return communityItem;
                })
            );
    }
}
